import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import { threadsPanelHide } from '@storeOld/reducers/ui'
import { put, select } from 'redux-saga/effects'

import { artBoardActions } from '../actions'
import { ModesExclusiveWithStructure } from '../constants'
import type { ArtBoardMode } from '../types'

// Everything built on `Panel` occupies the same right-hand slot: the structure
// sidebar, the artboard comments, project settings (with activity history,
// storage, colours and the symbol library) and the estimator. Two of them open
// at once would sit on top of each other, so opening one closes the others.
//
// The AI chat is pinned to the left edge and is deliberately not part of this.
//
// Only these directions are watchers: the legacy threadsPanelShow carries no
// typed action creator for redux-saga to match on, so that one direction closes
// the panel where the comments are opened (ProjectToolbar).

export function* closeOthersForStructure({
  payload,
}: ReturnType<typeof artBoardActions.showStructure>) {
  if (!payload.showStructure) {
    return
  }

  yield put(threadsPanelHide())

  const mode: ArtBoardMode = yield select(
    ({ artboard }: AppState) => artboard.mode
  )

  if (!ModesExclusiveWithStructure.includes(mode)) {
    return
  }

  // Leaving one of those modes means going back to the artboard itself — and
  // for someone without edit access that is `readonly`, never `edit`.
  const isReadOnly: boolean = yield select(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  yield put(artBoardActions.changeMode(isReadOnly ? 'readonly' : 'edit'))
}

// SEO mode is one-way here: it closes the panel (its own sidebar takes the
// slot, and the Structure button is hidden there), but opening the panel must
// never drop the user out of SEO — that mode is left by its own switch only.
export function* closeStructureForMode({
  payload,
}: ReturnType<typeof artBoardActions.changeMode>) {
  if (ModesExclusiveWithStructure.includes(payload) || payload === 'seo_mode') {
    yield put(artBoardActions.showStructure({ showStructure: false }))
  }
}
